import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { WorkflowStatusBadge, deriveWorkflowStatus } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Expense {
  id: number
  expense_date: string | null
  user_name: string | null
  vendor_name: string | null
  expense_type: string | null
  amount: number
  has_receipt: boolean
  receipt_url: string | null
  submitted: boolean
  approved: boolean
  paid: boolean
  billed: boolean
  can_edit: boolean
  can_approve: boolean
  can_delete: boolean
}

interface Summary {
  total_amount: number
  total_billable: number
  pending_approval: number
  pending_payment: number
  by_type: Record<string, number>
}

interface FilterOption {
  id: number
  name: string
}

interface Filters {
  start_date: string | null
  end_date: string | null
  user_id: number | null
  expense_type_ref_id: number | null
  vendor_id: number | null
}

interface FilterOptions {
  employees: FilterOption[]
  expense_types: FilterOption[]
  vendors: FilterOption[]
}

interface Props {
  case_record: CaseRecord
  expenses: Expense[]
  summary: Summary
  filters: Filters
  filter_options: FilterOptions
}

export default function Index({
  case_record,
  expenses,
  summary,
  filters,
  filter_options,
}: Props) {
  const [startDate, setStartDate] = useState(filters.start_date ?? "")
  const [endDate, setEndDate] = useState(filters.end_date ?? "")
  const [userId, setUserId] = useState(filters.user_id?.toString() ?? "")
  const [expenseTypeRefId, setExpenseTypeRefId] = useState(
    filters.expense_type_ref_id?.toString() ?? "",
  )
  const [vendorId, setVendorId] = useState(filters.vendor_id?.toString() ?? "")

  const applyFilters = () => {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (userId) params.user_id = userId
    if (expenseTypeRefId) params.expense_type_ref_id = expenseTypeRefId
    if (vendorId) params.vendor_id = vendorId
    router.get(`/cases/${case_record.id}/expenses`, params, {
      preserveState: true,
    })
  }

  const clearFilters = () => {
    setStartDate("")
    setEndDate("")
    setUserId("")
    setExpenseTypeRefId("")
    setVendorId("")
    router.get(`/cases/${case_record.id}/expenses`)
  }

  const hasActiveFilters =
    startDate || endDate || userId || expenseTypeRefId || vendorId

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Expenses", href: `/cases/${case_record.id}/expenses` },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this expense?")) {
      router.delete(`/cases/${case_record.id}/expenses/${id}`)
    }
  }

  const handleApprove = (id: number) => {
    router.patch(`/cases/${case_record.id}/expenses/${id}/approve`)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const expenseTypeName = (type: string | null) => {
    const types: Record<string, string> = {
      F: "Fuel",
      L: "Lodging",
      P: "Per Diem",
      R: "Rental Car",
      A: "Airfare",
      O: "Other",
    }
    return type ? types[type] || type : "-"
  }

  const statusBadge = (expense: Expense) => (
    <WorkflowStatusBadge status={deriveWorkflowStatus(expense)} />
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Expenses - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Expenses for {case_record.case_number}
          </h1>
          <Link
            href={`/cases/${case_record.id}/expenses/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Expense
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Total Amount</div>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.total_amount)}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Total Billable</div>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.total_billable)}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Pending Approval</div>
            <div className="text-2xl font-bold">{summary.pending_approval}</div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Pending Payment</div>
            <div className="text-2xl font-bold">{summary.pending_payment}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 rounded-lg bg-white p-4 shadow">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            {filter_options.employees.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Employee
                </label>
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
                >
                  <option value="">All Employees</option>
                  {filter_options.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {filter_options.expense_types.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Expense Type
                </label>
                <select
                  value={expenseTypeRefId}
                  onChange={(e) => setExpenseTypeRefId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
                >
                  <option value="">All Types</option>
                  {filter_options.expense_types.map((et) => (
                    <option key={et.id} value={et.id}>
                      {et.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {filter_options.vendors.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Company/Payee
                </label>
                <select
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
                >
                  <option value="">All Vendors</option>
                  {filter_options.vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={applyFilters}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white"
              >
                Filter
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-hpi-med-gray hover:text-hpi-dark-gray rounded border border-gray-300 px-4 py-1.5 text-sm"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {summary.pending_approval > 0 && (
          <div className="bg-hpi-yellow/20 text-hpi-dark-gray mb-4 rounded p-3">
            {summary.pending_approval}{" "}
            {summary.pending_approval === 1 ? "expense" : "expenses"} pending
            approval
          </div>
        )}

        {expenses.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Employee
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Company/Payee
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Amount
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Receipt
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/cases/${case_record.id}/expenses/${expense.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {expense.expense_date}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {expense.user_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {expense.vendor_name ?? "-"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {expenseTypeName(expense.expense_type)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {formatCurrency(expense.amount)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {expense.receipt_url ? (
                      <a
                        href={expense.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        Yes
                      </a>
                    ) : expense.has_receipt ? (
                      <span className="text-hpi-dark-green">Yes</span>
                    ) : (
                      <span className="text-hpi-med-gray">No</span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {statusBadge(expense)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/cases/${case_record.id}/expenses/${expense.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      View
                    </Link>
                    {expense.can_edit && (
                      <Link
                        href={`/cases/${case_record.id}/expenses/${expense.id}/edit`}
                        className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                      >
                        Edit
                      </Link>
                    )}
                    {expense.can_approve && !expense.approved && (
                      <button
                        onClick={() => handleApprove(expense.id)}
                        className="text-hpi-dark-green hover:text-hpi-dark-green mr-2"
                      >
                        Approve
                      </button>
                    )}
                    {expense.can_delete && (
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-hpi-red hover:text-hpi-red"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No expenses found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
